import { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiMultiSelect,
  MuiRadioGroup,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import {
  MAX_LENGTH_75,
  MDM_ACTIVITY_SEARCH_TYPE_KEY,
} from "../../../../helpers/constants";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  setActivityFramework,
  updateSelectedUnit,
} from "../../../../redux/slices/mdmActivity/mdmActivitySlice";
import { MdmAddActivityFormKeys } from "../../modelsAndSchemas/models";
import { mdmAddActivitySiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface AddActivityDetailProps {
  pageId: string;
  modalityRef: React.Ref<{ resetValues: (values?: string[]) => void }>;
  programFrameworkRef: React.Ref<{ resetValues: (values?: string[]) => void }>;
  projectFrameworkRef: React.Ref<{ resetValues: (values?: string[]) => void }>;
  programFrameWorkList: OptionsDataType[];
  projectFrameWorkList: OptionsDataType[];
  handleActivityNameOnBlur: (value: string) => void;
  onRadioToggle: (searchTypeValue: string) => void;
  isEditActivity?: boolean;
  searchType: string;
}

export const AddActivityDetail: FC<AddActivityDetailProps> = ({
  pageId,
  modalityRef,
  programFrameworkRef,
  projectFrameworkRef,
  programFrameWorkList,
  projectFrameWorkList,
  handleActivityNameOnBlur,
  onRadioToggle,
  isEditActivity,
  searchType,
}) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { control } = useFormContext();
  const dispatch = useDispatch();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getAddActivityLabel = useFetchLabel(
    mdmAddActivitySiteCoreKeys.activityDetailsKey,
  );

  const handleUnitChange = (value: string) => {
    dispatch(updateSelectedUnit(value));
  };

  useEffect(() => {
    dispatch(setActivityFramework(searchType));
  }, [searchType]);

  const isSearchTypeProgram = useMemo(
    () => searchType === MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
    [searchType],
  );
  const isSearchTypeProject = useMemo(
    () => searchType === MDM_ACTIVITY_SEARCH_TYPE_KEY.project,
    [searchType],
  );

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onRadioToggle(e.target.value);
  };

  const radioOptions = [
    {
      value: MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
      label: getAddActivityLabel(mdmAddActivitySiteCoreKeys.programRadioButton),
    },
    {
      value: MDM_ACTIVITY_SEARCH_TYPE_KEY.project,
      label: getAddActivityLabel(mdmAddActivitySiteCoreKeys.projectRadioButton),
    },
  ];

  return (
    <MuiBox className="add-activity-container">
      {!isEditActivity && (
        <MuiBox className="radio-container">
          <MuiRadioGroup
            label={getAddActivityLabel(
              mdmAddActivitySiteCoreKeys.selectFrameworkType,
            )}
            value={searchType}
            onChange={handleSearchTypeChange}
            options={radioOptions}
            requiredARIALabel={commonARIALabel.required}
          />
        </MuiBox>
      )}
      <MuiGrid container>
        {isSearchTypeProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={MdmAddActivityFormKeys.programFramework}
              ref={programFrameworkRef}
            >
              <MuiMultiSelect
                label={getAddActivityLabel(
                  mdmAddActivitySiteCoreKeys.programFramework,
                )}
                options={programFrameWorkList}
                showSearch
                data-testid="program-framework-select"
                showChip
                isMandatory
                id={`${pageId}-${MdmAddActivityFormKeys.programFramework}`}
                chipId={`${pageId}-${MdmAddActivityFormKeys.programFramework}-chip`}
                dialogTitle={getAddActivityLabel(
                  mdmAddActivitySiteCoreKeys.selectedProgramFramework,
                )}
                isDialogTitleWithEllipsis
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>
        )}

        {isSearchTypeProject && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={MdmAddActivityFormKeys.projectFramework}
              ref={projectFrameworkRef}
            >
              <MuiMultiSelect
                label={getAddActivityLabel(
                  mdmAddActivitySiteCoreKeys.projectFramework,
                )}
                options={projectFrameWorkList}
                data-testid="project-framework-select"
                showSearch
                isMandatory
                showChip
                chipId={`${pageId}-${MdmAddActivityFormKeys.projectFramework}-chip`}
                id={`${pageId}-${MdmAddActivityFormKeys.projectFramework}`}
                isDialogTitleWithEllipsis
                dialogTitle={getAddActivityLabel(
                  mdmAddActivitySiteCoreKeys.selectedProjectFramework,
                )}
                searchPlaceHolder={commonLabels.search}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              />
            </FormController>
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmAddActivityFormKeys.activityName}
          >
            <MuiTextField
              label={getAddActivityLabel(
                mdmAddActivitySiteCoreKeys.activityName,
              )}
              value=""
              id={`${pageId}-${MdmAddActivityFormKeys.activityName}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_75}
              isMandatory
              onBlur={(e) => handleActivityNameOnBlur(e.target.value)}
              requiredARIALabel={commonARIALabel.required}
              data-testid="topic-input"
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmAddActivityFormKeys.modality}
            ref={modalityRef}
          >
            <MuiMultiSelect
              label={getAddActivityLabel(mdmAddActivitySiteCoreKeys.modality)}
              options={fetchDropdownValues(
                mdmAddActivitySiteCoreKeys.activityDetailsKey,
                mdmAddActivitySiteCoreKeys.modalityList,
              )}
              showSearch
              showChip
              isMandatory
              id={`${pageId}-${MdmAddActivityFormKeys.modality}`}
              chipId={`${pageId}-${MdmAddActivityFormKeys.modality}-chip`}
              dialogTitle={getAddActivityLabel(
                mdmAddActivitySiteCoreKeys.selectedModality,
              )}
              data-testid="modality-select"
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={MdmAddActivityFormKeys.units}>
            <MuiSelect
              label={getAddActivityLabel(mdmAddActivitySiteCoreKeys.units)}
              options={fetchDropdownValues(
                mdmAddActivitySiteCoreKeys.activityDetailsKey,
                mdmAddActivitySiteCoreKeys.unitsList,
              )}
              value=""
              data-testid="units-select"
              id={`${pageId}-${MdmAddActivityFormKeys.units}`}
              placeholder={commonLabels.selectPlaceHolder}
              visibleDefaultSelectOption
              isMandatory
              onChange={(e) => handleUnitChange(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
